import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { User } from '../../types/auth';
import { Position } from '../../types/user';
import { getPositions, UpdateUserPayload } from '../../services/userApi';

interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (id: number, userData: UpdateUserPayload) => void;
    user: User | null;
}

export default function EditUserModal({ isOpen, onClose, onSubmit, user }: EditUserModalProps) {
    const [positions, setPositions] = useState<Position[]>([]);
    const [isLoadingPositions, setIsLoadingPositions] = useState(false);
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [formData, setFormData] = useState<{
        name: string;
        email: string;
        utype: 'ADMIN' | 'USER' | 'STORE';
        position_id?: number;
        password: string;
        password_confirmation: string;
    }>({
        name: '',
        email: '',
        utype: 'USER',
        position_id: undefined,
        password: '',
        password_confirmation: ''
    });

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name,
                email: user.email,
                utype: user.utype,
                position_id: user.position_id,
                password: '',
                password_confirmation: ''
            });
            setShowPasswordFields(false);
        }
    }, [user]);

    useEffect(() => {
        if (isOpen) {
            fetchPositions();
        }
    }, [isOpen]);

    const fetchPositions = async () => {
        try {
            setIsLoadingPositions(true);
            const response = await getPositions();
            setPositions(response.data);
        } catch (error) {
            console.error('Failed to fetch positions:', error);
        } finally {
            setIsLoadingPositions(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        const payload: UpdateUserPayload = {
            name: formData.name,
            email: formData.email,
            utype: formData.utype,
            position_id: formData.position_id
        };

        if (formData.password) {
            payload.password = formData.password;
            payload.password_confirmation = formData.password_confirmation;
        }

        onSubmit(user.id, payload);
        onClose();
    };

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 px-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 dark:border-gray-800">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Edit User</h2>
                    <button onClick={onClose} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                        <input
                            type="text"
                            required
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                        <input
                            type="email"
                            required
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                        <input
                            type="text"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            value={formData.utype}
                            disabled
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Position</label>
                        <select
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            value={formData.position_id ?? ''}
                            onChange={(e) => setFormData({ ...formData, position_id: e.target.value ? Number(e.target.value) : undefined })}
                        >
                            <option value="">Select a position</option>
                            {positions.map((position) => (
                                <option key={position.id} value={position.id}>
                                    {position.name} ({position.department?.name})
                                </option>
                            ))}
                        </select>
                        {isLoadingPositions && <span className="text-sm text-gray-500 dark:text-gray-400">Loading positions...</span>}
                    </div>

                    <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showPasswordFields}
                                onChange={(e) => setShowPasswordFields(e.target.checked)}
                                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                            />
                            Update password
                        </label>
                    </div>

                    {showPasswordFields && (
                        <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                                <input
                                    type="password"
                                    className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm Password</label>
                                <input
                                    type="password"
                                    className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                    value={formData.password_confirmation}
                                    onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                                />
                            </div>
                        </div>
                    )}

                    <div className="col-span-2 flex justify-end space-x-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded-md hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}